<template>
  <div class="cat-input">
    <cat-label
      v-if="!labelHandled"
      :label-id="id + '-input-lbl'"
      :label="label"
      :for-id="id"
      :optional="!mandatory"
      :display-optional="showOptionals"
      class="placeholder"
    ></cat-label>
    <input
      v-if="inputType !== 'textarea'"
      :id="id"
      v-model="model"
      v-facade="inputMask"
      :placeholder="placeholder"
      :data-label-type="placeholder ? 'placeholder' : null"
      :data-label="dataLabel"
      :type="inputType"
      :aria-invalid="errors"
      :aria-required="mandatory || null"
      :aria-label="offscreenLabel"
      :aria-describedby="ariaDescribedby || null"
      :readonly="readonly"
      :tabindex="readonly ? -1 : null"
      :required="errors"
      :maxlength="maxLength"
      :class="errors ? 'invalidField' : ''"
      :data-masked="inputMask ? 'true' : null"
      :disabled="disabled"
      :aria-disabled="disabled ? 'true' : null"
      :autocomplete="autofillToken"
      @change="_onChange"
      @input="_onInput"
      @blur="_onBlur"
      @keydown="_keydown"
      @paste="_onPaste"
      @focus="_onFocus"
      @keyup="_keyup"
      @keypress="_keypress"
    />
    <textarea
      v-else-if="inputType === 'textarea'"
      :id="id"
      v-model="model"
      :placeholder="placeholder"
      data-label-type="placeholder"
      :data-label="label ? label : placeholder"
      :aria-invalid="errors"
      :aria-required="mandatory"
      :aria-label="!labelHandled && label ? label : null"
      :aria-describedby="ariaDescribedby || null"
      :readonly="readonly"
      :required="errors"
      :maxlength="maxLength"
      :class="errors ? 'invalidField' : ''"
      :disabled="disabled || null"
      :aria-disabled="disabled ? 'true' : null"
      :autocomplete="autofillToken"
      @change="_onChange"
      @input="_onInput"
      @blur="_onBlur"
      @keydown="_keydown"
      @paste="_onPaste"
      @focus="_onFocus"
      @keyup="_keyup"
      @keypress="_keypress"
    ></textarea>
    <span
      v-if="remainingLabel"
      class="cat-input-sr-only visually-hidden"
      aria-live="polite"
      >{{ remainingLabel }}</span
    >
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import facade from "@/plugins/input-facade/directive.js";
import CatLabel from "@/components/utilities/cat-label";
import debounce from "lodash/debounce";

export default defineComponent({
  setup() {
    // Inject the global value
    const showOptionals = inject("showOptionals", true);
    const resolveTxt = inject("resolveTxt", (key, def) => def);

    return {
      showOptionals,
      resolveTxt
    };
  },
  name: "CatInput",
  directives: { facade },
  components: { CatLabel },
  props: {
    placeholder: {
      type: String,
      description: "Sets the placeholder text for a field."
    },
    dataLabel: [String],
    mandatory: {
      type: Boolean,
      description: "Sets aria-required attributes for input fields and label."
    },
    disabled: {
      type: Boolean,
      default: false,
      description:
        "Sets 'disabled' and 'area-disabled' attributes for the input fields elements."
    },
    id: [String, Number],
    label: {
      type: String,
      description: "Sets the label to display above input fields."
    },
    offscreenLabel: {
      type: String,
      default: null,
      description: "Sets 'aria-label' attribute for accessibility."
    },
    autofillToken: {
      type: String,
      description:
        "The token to be set to the autocomplete html markup attribute."
    },
    value: {
      type: [String, Number],
      description: "Defines the initial default value for the associated field."
    },
    errors: {
      type: Boolean,
      default: false
    },
    labelHandled: {
      type: Boolean,
      default: false,
      description:
        "Flag to indicate if requiring an input field label. True means the input label is already defined somewhere else. False means a field label will rendered for this input."
    },
    readonly: {
      type: [Boolean],
      description:
        "Defines whether a user can input data into the associated field."
    },
    inputType: {
      type: String,
      default: "text", // The usual input types and also textarea
      description:
        "Defines the type of data the field will accept from the standard HTML options for input. For example: text, number, password. For auto-format numbers, please see cat-attribute-number or cat-attribute-decimal."
    },
    mask: {
      type: String,
      description:
        "Defines characters which will be accepted by the input element. "
    },
    inputMask: {
      type: [String, Array],
      default: "",
      description:
        "The masking library we are using is vue-input-facade and go to https://www.npmjs.com/package/vue-input-facade to read the full documentation." +
        "Supported mask tokens are the folowwing: " +
        "S = alpha characters" +
        "# = numerical characters" +
        "X = alpha numerical characters" +
        "A = alpha characters, transformed to uppercase" +
        "a = alpha characters, transformed to lowercase"
    },
    regex: {
      type: String
    },
    maxLength: {
      type: Number,
      description: "Sets the maximum number of characters that can be entered."
    },
    title: {
      type: String
    },
    debounceTime: {
      type: Number,
      default: 300,
      description:
        "The recommend delay time is around 250m-300 (ms). This delay provides a short period of inactivity before triggering/sending the event."
    },
    ariaDescribedby: {
      type: String,
      default: "",
      description:
        "Sets the aria-describedby attribute to the input field. This should be used to point to an element that contains help text."
    }
  },
  data: function () {
    return {
      model: this.value,
      working: this.value,
      hasFocus: false,
      inputMaskChanged: false,
      // manualInput: false,
      keyWhitelist: [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Enter"
      ],
      remainingChars: -1
    };
  },
  watch: {
    value: function (newvalue) {
      // don't update model if in field and the change is done via manual input
      if (newvalue !== this.model && this.working === this.model) {
        this.model = newvalue;
        this.working = newvalue;
        // ensure that 'autofill-event' is up to date
        this.$el.querySelector("input,textarea").$$currentValue = newvalue;
      }
    },
    model: function (newvalue, oldvalue) {
      if (this.inputMaskChanged) {
        if (newvalue !== oldvalue) {
          // The emit ui-change is needed when the facade (inputMask) changes
          // to update the attribute value with the new facade changes
          this.$emit("ui-change", {
            value: this.model,
            id: this.id ? this.id : null
          });
        }
        this.inputMaskChanged = false;
      }
    },
    inputMask: function (newvalue, oldvalue) {
      if (newvalue !== oldvalue) {
        this.inputMaskChanged = true;
      }
    }
  },
  mounted: function () {
    const input = this.$el.querySelector("input, textarea");
    if (!this.value) {
      // need to check that checkAndTriggerAutoFillEvent is available before calling.
      // currently it isn't available to unit tests
      if (input && input.checkAndTriggerAutoFillEvent) {
        input.checkAndTriggerAutoFillEvent();
        input.setAttribute("value", "");
      }
    } else {
      // set value on attribute so autofill event doesn't fire mistakenly.
      input.setAttribute("value", this.model);
      // console.log('set value to ' + this.model, input);
    }
  },

  computed: {
    remainingLabel: function () {
      if (this.remainingChars >= 0) {
        let label = this.resolveTxt(
          "skin.input.remaining.label",
          "{0} characters left"
        );
        return label.replace(/\{0}/g, this.remainingChars);
      }
      return undefined;
    }
  },

  methods: {
    _handleDebounce: function (fn) {
      if (this.debounceTime <= 0) {
        return fn();
      } else {
        debounce(function () {
          return fn();
        }, this.debounceTime)();
      }
    },
    _keydown: function (e) {
      let prevented = false;
      if (
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        this.keyWhitelist.indexOf(e.key) === -1
      ) {
        // console.log("key down ", e, this.model);
        if (
          e.target.selectionStart == e.target.selectionEnd &&
          this.model &&
          this.maxLength &&
          this.model.length >= this.maxLength
        ) {
          e.preventDefault();
          prevented = true;
        } else if (this.mask && e.key.length == 1) {
          if (this.mask && this.mask.indexOf(e.key) == -1) {
            e.preventDefault();
            prevented = true;
          }
        }
      }
      if (!prevented) {
        this.$emit("ui-key-down", {
          event: e,
          value: this.model,
          id: this.id ? this.id : null
        });
      }
    },
    _keyup: function (event) {
      const _this = this;

      // Remaining characters only available when inputMask has value
      if (_this.model && _this.inputMask) {
        // Update remaining count
        _this._handleDebounce(function () {
          _this.remainingChars = _this.handleRemainingChars(_this.model);
        });
      }

      this.$emit("ui-key-up", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _keypress: function (event) {
      this.$emit("ui-key-press", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onChange: function (event) {
      if (!this.maskMatch(this.model)) {
        this.model = this.working;
        return false;
      }
      if (!this.regexMatch(this.model)) {
        this.model = this.working;
        return false;
      }
      // jQuery('#'+this.id).$$currentValue = this.model;
      // console.log("ui change firing", this.model, jQuery('#'+this.id))
      this.$emit("ui-change", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onFocus: function (event) {
      this.hasFocus = true;
      // this.manualInput = false;
      this.$emit("ui-focus", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onBlur: function (event) {
      this.hasFocus = false;
      // this.manualInput = false;
      this.$emit("ui-blur", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    _onPaste: function (e) {
      if (this.mask && e.clipboardData) {
        if (e.clipboardData && e.clipboardData.getData) {
          var pastedText = "";
          if (window.clipboardData && window.clipboardData.getData) {
            // IE
            pastedText = window.clipboardData.getData("Text");
          } else if (e.clipboardData && e.clipboardData.getData) {
            pastedText = e.clipboardData.getData("text/plain");
          }
        }
        if (!this.maskMatch(pastedText)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
      return true;
    },
    _onInput: function (event) {
      // this.manualInput = true;
      if (!this.maskMatch(event.target.value)) {
        this.model = this.working;
        return;
      }
      if (!this.regexMatch(event.target.value)) {
        this.model = this.working;
        return;
      }
      this.model = event.target.value; //two way binding
      this.working = this.model;

      // keep current value up to date on autofill or we get extra on change events
      // jQuery('#'+this.id).get(0).$$currentValue = this.model;
      this.$emit("ui-input", {
        event: event,
        value: this.model,
        id: this.id ? this.id : null
      });
    },
    maskMatch: function (val) {
      if (this.mask && val) {
        for (let i = 0; i < val.length; i++) {
          if (this.mask.indexOf(val.charAt(i)) == -1) {
            return false;
          }
        }
      }
      return true;
    },
    regexMatch: function (val) {
      if (this.$props.regex) {
        let re = RegExp(this.$props.regex);
        return re.test(val);
      }
      return true;
    },
    handleRemainingChars: function (val) {
      const valueLength = strLength(val, false);
      const max = strLength(this.inputMask, true); // TO add if we want to || this.maxLength

      return max - valueLength;

      // String count
      function strLength(str, maskFormat) {
        if (maskFormat) {
          // Replace special characters and spaces (except S, #, X, A, a) with empty string
          return str.replace(/[^S#XAa]/g, "").length;
        } else {
          // Replace special characters and spaces with empty string
          return str.replace(/[^\w\s]/gi, "").replace(/\s+/g, "").length;
        }
      }
    }
  }
});
</script>
